import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useCompanyScopeContext } from "@ryde/redux";
import {
  errors,
  GlobalAppEventEnum,
  Log,
  type GlobalAppEvents,
  type IAbortBookingPollingEventArgs,
  type IBulkSetBookingsDto,
  type IBulkUpdateBookingsDateTimeDto,
} from "@ryde/shared";

export const useBookingsPollingHandlingActions = () => {
  const { t } = useTranslation();
  const { eventEmitter } = useCompanyScopeContext();

  const emitOrWarn = useEvent(
    <TEvent extends keyof GlobalAppEvents>({
      event,
      payload,
      logMessage,
    }: {
      event: TEvent;
      payload: GlobalAppEvents[TEvent];
      logMessage: string;
    }): void => {
      if (!eventEmitter) {
        Log.warnAndToast(
          t(
            errors.common.technicalProblemOccurred,
            "Event emitter not initiated",
          ),
        );
        return;
      }

      Log.debug(logMessage, payload);
      eventEmitter.emit(event, payload);
    },
  );

  const initBookingPollingHandler = useEvent(
    (payload: IBulkSetBookingsDto): void =>
      emitOrWarn({
        event: GlobalAppEventEnum.INIT_BOOKINGS_POLLING,
        payload,
        logMessage: "Initiating booking polling",
      }),
  );

  const initBookingsDateTimeUpdatePollingHandler = useEvent(
    (payload: IBulkUpdateBookingsDateTimeDto): void =>
      emitOrWarn({
        event: GlobalAppEventEnum.INIT_BOOKINGS_DATE_TIME_UPDATE_POLLING,
        payload,
        logMessage: "Initiating bookings date and time update polling",
      }),
  );

  const abortBookingCreateHandler = useEvent(
    (payload: IAbortBookingPollingEventArgs): void =>
      emitOrWarn({
        event: GlobalAppEventEnum.ABORT_BOOKINGS_POLLING,
        payload,
        logMessage: "Aborting booking polling",
      }),
  );

  return {
    initBookingPollingHandler,
    initBookingsDateTimeUpdatePollingHandler,
    abortBookingCreateHandler,
  };
};
